import { XLSX } from './xlsxShim';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getUser } from '../variables/constants';
import i18n from '../i18n';
import { localeIntl } from '../i18n/format';

import logoBncb from '../assets/images/logo.png';

/**
 * Export utilities - Excel & PDF "smart" exports.
 *
 * Format attendu pour `columns` :
 *   [
 *     { title: 'N° Sinistre', dataIndex: 'numeroSinistreLocal' },
 *     { title: 'Montant', dataIndex: 'montant', format: 'currency' },
 *     { title: 'Date', dataIndex: 'date', format: 'date' },
 *     { title: 'Statut', dataIndex: 'statut', render: (v) => STATUT_MAP[v]?.label || v },
 *   ]
 *
 * Les colonnes JSX (icônes, tags, boutons d'action) doivent être exclues via
 * `exportable: false` - on ne peut pas les exporter proprement.
 */

const GREEN = [26, 107, 60]; // #1a6b3c
// Exports internes : ils suivent la langue de l'interface (referentiels:export).
const bncbTitre = () => i18n.t('referentiels:export.titreBncb');

const pad = (n) => String(n).padStart(2, '0');

const nowLabel = () => {
  const d = new Date();
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const slugDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fmtMontant = (v) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // Espace ASCII (jsPDF ne rend pas correctement le narrow-no-break-space
  // produit par Intl.NumberFormat('fr-FR'), d'où "4 0 0 / 0 0 0" au lieu de "400 000").
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const entier = Math.trunc(abs);
  const entierFmt = String(entier).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const decimales = abs - entier;
  if (decimales > 0) {
    const decStr = decimales.toFixed(2).slice(2);
    return `${sign}${entierFmt},${decStr}`;
  }
  return `${sign}${entierFmt}`;
};

const fmtDate = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString(localeIntl()); } catch { return String(v); }
};

const fmtDateTime = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(localeIntl()); } catch { return String(v); }
};

/** Applique `render` ou `format` à une valeur pour la rendre exportable (string). */
const formatValue = (raw, col, record, index) => {
  if (col.export) return col.export(raw, record, index);
  if (col.format === 'currency') return fmtMontant(raw);
  if (col.format === 'date') return fmtDate(raw);
  if (col.format === 'datetime') return fmtDateTime(raw);
  if (col.format === 'bool') return raw ? i18n.t('referentiels:export.oui') : i18n.t('referentiels:export.non');
  if (typeof col.render === 'function') {
    // On tente render(value, record, index) mais on refuse le JSX
    const rendered = col.render(raw, record || {}, index || 0);
    if (rendered == null) return '';
    if (typeof rendered === 'string' || typeof rendered === 'number') return String(rendered);
    return String(raw ?? '');
  }
  return raw == null ? '' : String(raw);
};

/** Ne garde que les colonnes exportables (avec `dataIndex` ou `key`). */
const filterColumns = (columns) =>
  (columns || []).filter(c => c && c.exportable !== false && (c.dataIndex || c.key) && c.title);

/** Retourne la valeur brute via dataIndex (accepte tableau pour accès imbriqué). */
const getRawValue = (record, dataIndex) => {
  if (!dataIndex) return null;
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, k) => (acc == null ? null : acc[k]), record);
  }
  return record[dataIndex];
};

const isNumericCol = (col) => col.format === 'currency' || col.numeric === true;

/** Ajoute l'unité "(FCFA)" dans l'entête des colonnes monétaires. */
const colTitleWithUnit = (col) => {
  if (col.format === 'currency') return `${col.title} (FCFA)`;
  return col.title;
};

const computeTotals = (rows, cols) => {
  const totals = {};
  for (const c of cols) {
    if (!isNumericCol(c)) continue;
    totals[c.title] = rows.reduce((s, r) => {
      const v = Number(getRawValue(r, c.dataIndex));
      return s + (Number.isFinite(v) ? v : 0);
    }, 0);
  }
  return totals;
};

// ═ EXCEL ═

export const exportToExcel = async ({ data, columns, filename, title, subtitle, showTotals = true }) => {
  const cols = filterColumns(columns);
  if (cols.length === 0) return;

  const user = getUser();
  const userLabel = user ? `${user.prenom || ''} ${user.nom || ''}`.trim() || user.login || '-' : '-';

  //  Lignes d'en-tête métadonnées 
  const headerRows = [
    [bncbTitre()],
    [title || i18n.t('referentiels:export.titreParDefaut')],
    ...(subtitle ? [[subtitle]] : []),
    [i18n.t('referentiels:export.exporteLePar', { date: nowLabel(), utilisateur: userLabel })],
    [i18n.t('referentiels:export.lignes', { count: data.length })],
    [], // ligne vide
  ];

  // ── En-têtes de colonnes + données ──
  const colTitles = cols.map(c => c.title);
  const dataRows = data.map((record, index) =>
    cols.map(c => {
      const raw = getRawValue(record, c.dataIndex);
      if (isNumericCol(c)) {
        const n = Number(raw);
        return Number.isFinite(n) ? n : '';
      }
      return formatValue(raw, c, record, index);
    })
  );

  //  Ligne totaux si applicable
  const totals = showTotals ? computeTotals(data, cols) : {};
  const hasTotals = Object.keys(totals).length > 0;
  const totalRow = hasTotals
    ? cols.map((c, i) => i === 0 ? 'TOTAL' : (isNumericCol(c) ? totals[c.title] : ''))
    : null;

  const aoa = [
    ...headerRows,
    colTitles,
    ...dataRows,
    ...(totalRow ? [totalRow] : []),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Largeur de colonnes auto
  ws['!cols'] = cols.map((c, i) => {
    const maxLen = Math.max(
      c.title.length,
      ...dataRows.map(r => String(r[i] ?? '').length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
  });

  // Style header row (XLSX ne supporte pas les styles sans bibliothèque pro,
  // mais on peut marquer les cellules comme header pour certains readers)
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: cols.length - 1 } },
  ];

  // Format numérique pour colonnes currency
  const headerOffset = headerRows.length + 1; // +1 pour ligne titres colonnes
  cols.forEach((c, i) => {
    if (!isNumericCol(c)) return;
    for (let r = 0; r < dataRows.length; r++) {
      const cell = XLSX.utils.encode_cell({ r: headerOffset + r, c: i });
      if (ws[cell]) ws[cell].z = '#,##0';
    }
    if (totalRow) {
      const cell = XLSX.utils.encode_cell({ r: headerOffset + dataRows.length, c: i });
      if (ws[cell]) ws[cell].z = '#,##0';
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (title || i18n.t('referentiels:export.nomFeuille')).slice(0, 30));

  const fname = (filename || 'export').replace(/\.xlsx?$/i, '');
  await XLSX.writeFile(wb, `${fname}_${slugDate()}.xlsx`);
};

// PDF

/**
 * Charge une image (URL webpack ou dataURL) et retourne { dataUrl, dims }.
 * Retourne null si logoSrc est absent ou en cas d'erreur.
 */
const loadLogoDataUrl = async (logoSrc) => {
  if (!logoSrc) return null;
  let dataUrl = logoSrc;
  if (!(typeof logoSrc === 'string' && logoSrc.startsWith('data:'))) {
    try {
      const res = await fetch(logoSrc);
      const blob = await res.blob();
      dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }
  // Read natural dimensions
  try {
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    return { dataUrl, dims };
  } catch {
    return { dataUrl, dims: null };
  }
};

/**
 * `logo` A UN DEFAUT, ET CE N'EST PAS UNE COMMODITE.
 *
 * Il etait facultatif sans defaut : les ecrans qui appellent cette fonction
 * DIRECTEMENT - la balance, les schemas d'ecriture, la fiscalite - produisaient des
 * PDF sans logo, a cote de ceux qui en portaient un. Rien ne le signalait :
 * `loadLogoDataUrl` rend `null` sans bruit quand la source manque.
 *
 * Le defaut avait deja change de camp dans `TableExportButton`, mais la correction
 * n'y valait que pour ses propres appelants. Posee ici, elle vaut pour tous.
 *
 * Un document officiel du Bureau porte son logo ; c'est donc l'ABSENCE qui doit se
 * declarer, en passant explicitement `logo={null}`.
 */
export const exportToPdf = async ({ data, columns, filename, title, subtitle, orientation,
  logo = logoBncb, logoPosition = 'left', didParseCell, showTotals = true }) => {
  const cols = filterColumns(columns);
  if (cols.length === 0) return;

  const user = getUser();
  const userLabel = user ? `${user.prenom || ''} ${user.nom || ''}`.trim() || user.login || '-' : '-';

  // Orientation auto : paysage si plus de 6 colonnes
  const orient = orientation || (cols.length > 6 ? 'landscape' : 'portrait');
  const doc = new jsPDF({ orientation: orient, unit: 'mm', format: 'a4' });

  const marginX = 14;
  let cursorY = 12;

  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  const rightX = pageWidth - marginX;

  //  Logo — en haut à gauche par défaut, centré au-dessus des titres si demandé
  const logoResult = await loadLogoDataUrl(logo);
  // Ce dont les titres descendent pour laisser la place au logo centré.
  let decalageTitres = 0;
  if (logoResult?.dataUrl) {
    try {
      // Largeur cible 30mm, hauteur calculée via ratio (max 25mm pour ne pas déborder).
      let logoW = 30;
      let logoH = 25;
      if (logoResult.dims && logoResult.dims.width > 0 && logoResult.dims.height > 0) {
        const ratio = logoResult.dims.height / logoResult.dims.width;
        logoH = Math.min(logoW * ratio, 25);
        logoW = logoH / ratio;
      }
      const centre = logoPosition === 'center';
      doc.addImage(logoResult.dataUrl, 'PNG', centre ? centerX - logoW / 2 : marginX, cursorY, logoW, logoH);
      // Centré, le logo occupe la place des titres : on les décale sous lui plutôt
      // que d'écrire par-dessus.
      if (centre) decalageTitres = logoH + 3;
    } catch { /* skip si format non supporté */ }
  }

  //  Date d'export + utilisateur (haut-droite) 
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.setFont('helvetica', 'italic');
  doc.text(i18n.t('referentiels:export.exporteLe', { date: nowLabel() }), rightX, cursorY + 5, { align: 'right' });
  doc.text(i18n.t('referentiels:export.lignes', { count: data.length }), rightX, cursorY + 15, { align: 'right' });

  //  Titre BNCB centré 
  doc.setFontSize(13);
  doc.setTextColor(...GREEN);
  doc.setFont('helvetica', 'bold');
  doc.text(bncbTitre(), centerX, cursorY + 6 + decalageTitres, { align: 'center' });

  //  Titre du rapport centré 
  doc.setFontSize(11);
  doc.setTextColor(30);
  doc.setFont('helvetica', 'bold');
  doc.text(title || i18n.t('referentiels:export.titreParDefaut'), centerX, cursorY + 13 + decalageTitres, { align: 'center' });

  //  Subtitle centré 
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, centerX, cursorY + 19 + decalageTitres, { align: 'center' });
  }

  //  Ligne séparatrice 
  const lineY = cursorY + (subtitle ? 24 : 20) + decalageTitres;
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.4);
  doc.line(marginX, lineY, rightX, lineY);

  // ── Données ──
  const head = [cols.map(c => c.title)];
  const body = data.map((record, index) =>
    cols.map(c => {
      const raw = getRawValue(record, c.dataIndex);
      return formatValue(raw, c, record, index);
    })
  );

  //  Totaux
  const totals = showTotals ? computeTotals(data, cols) : {};
  const hasTotals = Object.keys(totals).length > 0;
  const foot = hasTotals
    ? [cols.map((c, i) => i === 0 ? 'TOTAL' : (isNumericCol(c) ? fmtMontant(totals[c.title]) : ''))]
    : undefined;

  autoTable(doc, {
    startY: lineY + 4,
    head,
    body,
    foot,
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    footStyles: { fillColor: [232, 245, 237], textColor: GREEN, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    margin: { top: 15, left: 10, right: 10, bottom: 15 },
    didParseCell: didParseCell ? (data) => {
      didParseCell(data);
    } : undefined,
    didDrawPage: (d) => {
      // Footer pagination
      const pages = doc.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont('helvetica', 'normal');
      doc.text(
        i18n.t('referentiels:export.page', { page: d.pageNumber, pages }),
        pageWidth - 25, pageHeight - 8
      );
      doc.text(i18n.t('referentiels:export.confidentiel'), 10, pageHeight - 8);
    },
  });

  const fname = (filename || 'export').replace(/\.pdf$/i, '');
  doc.save(`${fname}_${slugDate()}.pdf`);
};
